/** @jsx jsx */
import { useCallback, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { useMediaQuery } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ChallengeDatesType, ICustomTheme } from 'interfaces';
import ErrorRedirectionView from 'components/ErrorRedirectionView';
import { Banner } from 'components';

import DeclarationForm from 'components/DeclarationForm';
import { LOADING_STATUS, ROUTES } from 'constants/index';
import { challengeSelector } from 'reducers/challenge.reducer';
import { userChallengeDeclaration } from 'actions/challenge.actions';
import {
  ErrorContainer,
  StyledCircularProgress,
  StyledCircularProgressContainer,
} from './styles';
import { getDates } from 'components/DeclarationForm/utils';
import { useImgSrc } from 'core/utils/use-img-src.hook';

export const ChallengeDeclaration = (): JSX.Element => {
  const { id: challengeIdString }: { id: string } = useParams();
  const challengeId = Number(challengeIdString);
  const urlActionDetail: string = `${ROUTES.CHALLENGE}/${challengeIdString}`;

  const dispatch = useDispatch();
  const {
    data: challengeData,
    status: challengeStatus,
    error: challengeError,
  } = useSelector(challengeSelector(challengeId));

  const fetchDeclarations = useCallback(() => {
    if (challengeId) {
      dispatch(userChallengeDeclaration.request({ challengeId }));
    }
  }, [challengeId, dispatch]);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const isMobile = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );

  const isLoadingChallenge =
    !challengeStatus ||
    challengeStatus === LOADING_STATUS.IDLE ||
    challengeStatus === LOADING_STATUS.PENDING;

  const isAlreadyDeclared =
    challengeData &&
    !challengeData.isDeclarationMultiple &&
    (challengeData.countDeclarationsValidated ||
      challengeData.countDeclarationsWaiting);

  const isChallengeOutOfDate = [
    challengeData?.endDate,
    challengeData?.endDiffusionDate,
  ].some((date) => date && new Date(date).getTime() < Date.now());

  const dates: ChallengeDatesType = getDates(challengeData, language);

  const { fullUri } = useImgSrc(challengeData?.image) || {};

  useEffect(() => {
    fetchDeclarations();
  }, [fetchDeclarations]);

  if (isLoadingChallenge) {
    return (
      <StyledCircularProgressContainer>
        <StyledCircularProgress />
      </StyledCircularProgressContainer>
    );
  }

  if (challengeStatus === LOADING_STATUS.REJECTED) {
    return challengeError?.response?.status === 403 ? (
      <ErrorContainer>
        <ErrorRedirectionView
          errorText={t('challenges.declaration.notTargetedByAction')}
          actionText={t('core.go_back_home')}
          buttonText={t('menu.home')}
          redirection={ROUTES.HOME}
          isMobile={isMobile}
        />
      </ErrorContainer>
    ) : (
      <ErrorContainer>
        <ErrorRedirectionView
          errorText={t('core.error_occured')}
          actionText={t('core.go_back_home')}
          buttonText={t('menu.home')}
          redirection={ROUTES.HOME}
          isMobile={isMobile}
        />
      </ErrorContainer>
    );
  }

  if (isChallengeOutOfDate) {
    return (
      <ErrorContainer>
        <ErrorRedirectionView
          errorText={t('challenges.declaration.outOfDateMessage')}
          actionText={t('challenges.declaration.detailPageRedirection')}
          buttonText={t('challenges.declaration.detailPageRedirectionButton')}
          redirection={urlActionDetail}
          isMobile={isMobile}
        />
      </ErrorContainer>
    );
  }

  if (isAlreadyDeclared) {
    return (
      <ErrorContainer>
        <ErrorRedirectionView
          errorText={t('challenges.declaration.alreadyDeclared')}
          actionText={t('challenges.declaration.detailPageRedirection')}
          buttonText={t('challenges.declaration.detailPageRedirectionButton')}
          redirection={urlActionDetail}
          isMobile={isMobile}
        />
      </ErrorContainer>
    );
  }

  return (
    <div>
      <Banner
        challengeType={challengeData.type}
        dates={dates}
        half={false}
        image={fullUri}
        isParticipating
      />
      <DeclarationForm challengeData={challengeData} />
    </div>
  );
};
